// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Input,
    InputOnChangeData,
    makeStyles,
    mergeClasses,
    shorthands,
    Subtitle2Stronger,
    tokens,
} from '@fluentui/react-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useChat, useFile } from '../../../libs/hooks';
import { getFriendlyChatName } from '../../../libs/hooks/useChat';
import { AlertType } from '../../../libs/models/AlertType';
import { ChatArchive } from '../../../libs/models/ChatArchive';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { addAlert } from '../../../redux/features/app/appSlice';
import { ChatListSortOptions, FeatureKeys } from '../../../redux/features/app/AppState';
import { Conversations } from '../../../redux/features/conversations/ConversationsState';
import { Breakpoints } from '../../../styles';
import { FileUploader } from '../../FileUploader';
import { Dismiss20, Filter20 } from '../../shared/BundledIcons';
import { isToday, isInLastWeek, isInLastMonth } from '../../utils/TextUtils';
import { NewBotMenu } from './bot-menu/NewBotMenu';
import { SimplifiedNewBotMenu } from './bot-menu/SimplifiedNewBotMenu';
import { ChatListSection } from './ChatListSection';
import { ChatState } from '../../../redux/features/conversations/ChatState';
import { SortChatMenu } from './SortChatMenu';
import { useSelector } from 'react-redux';
import {
    editConversationTitle,
    removeChatToGenerateTitleFor,
} from '../../../redux/features/conversations/conversationsSlice';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexShrink: 0,
        width: '320px',
        backgroundColor: tokens.colorNeutralBackground4,
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
        ...Breakpoints.small({
            width: '64px',
        }),
    },
    list: {
        overflowY: 'auto',
        overflowX: 'hidden',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colorScrollbarOverlay,
                visibility: 'visible',
            },
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorSubtleBackground,
        },
        alignItems: 'stretch',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginRight: tokens.spacingVerticalM,
        marginLeft: tokens.spacingHorizontalXL,
        marginTop: tokens.spacingVerticalS,
        alignItems: 'center',
        height: '60px',
        ...Breakpoints.small({
            justifyContent: 'center',
        }),
    },
    title: {
        flexGrow: 1,
        ...Breakpoints.small({
            display: 'none',
        }),
        fontSize: tokens.fontSizeBase500,
    },
    input: {
        flexGrow: 1,
        ...shorthands.padding(tokens.spacingHorizontalNone),
        ...shorthands.border(tokens.borderRadiusNone),
        backgroundColor: tokens.colorSubtleBackground,
        fontSize: tokens.fontSizeBase500,
    },
});

type ConversationsView = Record<string, Conversations | undefined>;

export const ChatList: FC = () => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const { chatListSortOption } = useAppSelector((state: RootState) => state.app);

    const [isFiltering, setIsFiltering] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        Today: conversations,
    });

    const chat = useChat();
    const fileHandler = useFile();
    const dispatch = useAppDispatch();

    function getLastUpdatedTimestamp(conversation: ChatState): number | undefined {
        return conversation.messages.length
            ? conversation.messages[conversation.messages.length - 1].timestamp
            : undefined;
    }

    const sortConversationsByDate = (conversations: Conversations): ConversationsView => {
        // Add conversations to sortedConversations in the order of sortedIds.
        const todayConversations: Conversations = {};
        const lastWeekConversations: Conversations = {};
        const lastMonthConversations: Conversations = {};
        const olderConversations: Conversations = {};
        Object.keys(conversations).forEach((id) => {
            if (isToday(new Date(getLastUpdatedTimestamp(conversations[id]) ?? 0))) {
                todayConversations[id] = conversations[id];
            } else if (isInLastWeek(new Date(getLastUpdatedTimestamp(conversations[id]) ?? 0))) {
                lastWeekConversations[id] = conversations[id];
            } else if (isInLastMonth(new Date(getLastUpdatedTimestamp(conversations[id]) ?? 0))) {
                lastMonthConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            Today: todayConversations,
            'Last 7 Days': lastWeekConversations,
            'Last 30 Days': lastMonthConversations,
            Older: olderConversations,
        };
    };

    const sortConversationsByPersona = (conversations: Conversations): ConversationsView => {
        const sortedConversations: ConversationsView = {};
        Object.keys(conversations).forEach((id) => {
            const personaName = conversations[id].personaName ?? 'Litium';

            // If this personaName does not yet exist in sortedConversations, create it
            if (!sortedConversations[personaName]) {
                sortedConversations[personaName] = {};
            }

            // Add the conversation to the appropriate personaName in sortedConversations
            const personaConversations = sortedConversations[personaName];
            if (personaConversations) {
                personaConversations[id] = conversations[id];
            }
        });

        // Resort the conversation collections by personaName in alphabetical order
        const keys = Object.keys(sortedConversations).sort();
        const reSortedConversations: ConversationsView = {};
        for (const key of keys) {
            reSortedConversations[key] = sortedConversations[key];
        }

        return reSortedConversations;
    };

    useEffect(() => {
        // Ensure local component state is in line with app state.
        const nonHiddenConversations: Conversations = {};
        for (const key in conversations) {
            const conversation = conversations[key];
            if (
                !conversation.hidden &&
                (!filterText ||
                    getFriendlyChatName(conversation).toLocaleUpperCase().includes(filterText.toLocaleUpperCase()))
            ) {
                nonHiddenConversations[key] = conversation;
            }
        }

        switch (chatListSortOption) {
            case ChatListSortOptions.Date:
                setConversationsView(sortConversationsByDate(nonHiddenConversations));
                break;
            case ChatListSortOptions.Persona:
                setConversationsView(sortConversationsByPersona(nonHiddenConversations));
                break;
        }
    }, [conversations, filterText, chatListSortOption]);

    const onFilterClick = () => {
        setIsFiltering(true);
    };

    const onFilterCancel = () => {
        setFilterText('');
        setIsFiltering(false);
    };

    const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.preventDefault();
        setFilterText(data.value);
    };

    const fileUploaderRef = useRef<HTMLInputElement>(null);
    const onUpload = useCallback(
        (file: File) => {
            fileHandler.loadFile<ChatArchive>(file, chat.uploadBot).catch((error) =>
                dispatch(
                    addAlert({
                        message: `Failed to parse uploaded file. ${error instanceof Error ? error.message : ''}`,
                        type: AlertType.Error,
                    }),
                ),
            );
        },
        [fileHandler, chat, dispatch],
    );

    const chatsToGenerateTitleFor = useSelector(
        (state: RootState): string[] => state.conversations.chatsToGenerateTitleFor,
    );
    const inProgressChatIds = useRef(new Set<string>());

    useEffect(() => {
        Promise.all(
            chatsToGenerateTitleFor.map(async (chatId: string) => {
                if (inProgressChatIds.current.has(chatId)) {
                    return;
                }
                inProgressChatIds.current.add(chatId);

                try {
                    const chatTitle = await chat.generateChatTitle(chatId);
                    if (chatTitle) {
                        dispatch(editConversationTitle({ id: chatId, newTitle: chatTitle }));
                    }

                    dispatch(removeChatToGenerateTitleFor({ chatId: chatId }));
                } catch (error) {
                    console.error('Error occurred while generating chat titles:', error);
                } finally {
                    inProgressChatIds.current.delete(chatId);
                }
            }),
        ).catch((error) => {
            console.error('Error occurred while generating chat titles:', error);
        });
    }, [chatsToGenerateTitleFor]);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {features[FeatureKeys.SimplifiedExperience].enabled ? (
                    <>
                        <SimplifiedNewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                        <SortChatMenu />
                    </>
                ) : (
                    <>
                        {!isFiltering && (
                            <>
                                <Subtitle2Stronger className={classes.title}>Conversations</Subtitle2Stronger>
                                <Button icon={<Filter20 />} appearance="transparent" onClick={onFilterClick} />
                                <NewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                                <FileUploader
                                    ref={fileUploaderRef}
                                    acceptedExtensions={['.json']}
                                    onSelectedFile={onUpload}
                                />
                            </>
                        )}
                        {isFiltering && (
                            <>
                                <Input
                                    placeholder="Filter by name"
                                    className={mergeClasses(classes.input, classes.title)}
                                    onChange={onSearch}
                                    autoFocus
                                />
                                <Button icon={<Dismiss20 />} appearance="transparent" onClick={onFilterCancel} />
                            </>
                        )}
                    </>
                )}
            </div>
            <div aria-label={'chat list'} className={classes.list}>
                {Object.keys(conversationsView).map((key) => {
                    const conversations = conversationsView[key];
                    return <ChatListSection key={key} header={key} conversations={conversations} />;
                })}
            </div>
        </div>
    );
};
